/**
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionResult} SubscriptionResult
 * @typedef {import('@/lib/filters/subscriptions').SubscriptionEvent} SubscriptionEvent
 * @typedef {import('@/lib/classes/InfoWindowContent').DisplayField} DisplayField
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').Comp} Comp
 */

import {EventHandling} from '@/lib/classes/EventHandling';
import {displayFieldsToObjects} from '@/lib/classes/InfoWindowContent/DisplayField';

/**
 * @typedef {object} GraphicExport An object containing a database-storable set of data relating to this graphic.
 * @property {object} graphicData All of the data necessary to recreate this graphic as-is.
 * @property {object} bounds The north/south/east/west GPS extents of this graphic. This may be the literal bounds of
 * the graphic if the underlying Google Maps API type provides that, or calculated synthetically if it does not.
 * @property {number} bounds.north Longitude of the north-most point of this graphic.
 * @property {number} bounds.south Longitude of the south-most point of this graphic.
 * @property {number} bounds.east Latitude of the east-most point of this graphic.
 * @property {number} bounds.west Latitude of the west-most point of this graphic.
 * @property {DisplayField[]} displayFields An array of fields representing user-facing data to be presented when an
 * end-user inspects this graphic, usually via an InfoWindow.
 * @property {Comp[]} comps An array of fields representing user-facing data to be presented when an
 * end-user inspects this graphic, usually via an InfoWindow.
 */

/**
 * @typedef {object} EditingContext
 * @property {Event} event The DOM / Maps event that triggered the call to setEditing.
 * @property {boolean} openInEditMode Whether the graphic should be opened in edit mode.
 */

class Graphic extends EventHandling {
  /**
   * The base class for all graphics. Implements nothing itself, but provides placeholder functions so child types can
   * inherit and not implement functions which don't make sense for the child.
   *
   * All types must implement the following methods: `constructor`, `setMap`
   *
   * - Editable types must also implement the following: `setEditing`, `subscribe`, and `toJSON`
   * - Highlightable types must also implement the following: `setHighlight`
   * - Clickable types must also implement the following: `setClickable`, `addHandler`
   *
   * Additionally, any editable type must also publish the `graphic_changed` event to all subscribers.
   * @param {object} [graphicData]
   */
  constructor(graphicData) {
    super();
    /** @type {DisplayField[]} */
    this.displayFields = [];
  }

  /**
   * Adds a new event handler to this graphic.
   * @param {string} eventType The type of event for which to invoke the handler.
   * @param {string} handlerName A name identifying this handler for later removal.
   * @param {Function} handler The handler to be invoked.
   */
  addHandler(eventType, handlerName, handler) {
    super.addHandler(eventType, handlerName, handler);
  }

  /**
   * Removes a previously-registered event handler from this graphic.
   * @param {string} eventType The type of event the handler is invoked for.
   * @param {string} handlerName The name identifying the handler to remove.
   * @param {Function} handler The handler to remove.
   */
  removeHandler(eventType, handlerName, handler) {
    super.removeHandler(eventType, handlerName, handler);
  }

  /**
   * Sets the clickable state of the graphic. Does NOT add click handlers, only functions to allow/prevent any click
   * handlers from being triggered.
   * @param {boolean} clickable If `true`, this graphic will be clickable on the map; otherwise it will not.
   * @return {void}
   */
  setClickable(clickable) {}
  /**
   * Indicates whether the graphic can be clicked.
   * Graphic subclasses that implement `setClickable` also need to implement this.
   * @return {boolean} `true` if the graphic can be clicked; otherwise `false`.
   */
  isClickable() {
    console.warn('Graphic.isClickable() is not implemented for', this);
    return false;
  }

  /**
   * Sets the graphic to be visible on the map or not. Calling this function with a `google.maps.Map` makes the graphic
   * visible on that map. Calling this function with `null` removes the graphic from the map where it is visible.
   * @param {google.maps.Map|null} map The map to attach this graphic to, or `null` to detach the graphic from its
   * current map.
   * @return {void}
   */
  setMap(map) {
    throw new Error('Graphic.setMap() is not implemented');
  }

  /**
   * Subscribes to an event on the graphic.
   *
   * All editable graphics publish the `graphic_changed` event to all subscribers. The `graphic_changed` event
   * is published only when changes that should be persisted have been made. This does not include things like
   * the highlight state, or the clickable state. The `graphic_changed` event is also be published sparingly,
   * no more than once per second.
   * Editable graphics will also likely publish the `open_info_window` event when the graphic wants to open a toolbar.
   * @param {string} eventName
   * @param {Function} callback
   * @return {SubscriptionResult}
   */
  subscribe(eventName, callback) {
    console.warn('Graphic.subscribe() is not implemented for this type');
    return {
      unsubscribe: () => {},
      setSuspended: () => {},
    };
  }

  /**
   * Converts the graphic to a GraphicExport object.
   * The `graphicData` property includes only the properties that are required to rehydrate the graphic from the
   * database, and those properties are in a format compatible with Firestore.
   * The `bounds` property includes the north/south/east/west GPS extents of the graphic, even if those are not an
   * inherent part of the underlying Google Maps API type.
   * The `displayFields` Includes any displayFields WHICH HAVE CHANGED. If no fields have changed due to the user
   * editing the graphic, the `displayFields` property may be omitted, null, or an empty array. Do NOT overwrite
   * existing `displayFields` with this value, it must always be merged to prevent data loss.
   * @return {GraphicExport}
   */
  toJSON() {
    const json = {};

    if (this.displayFields !== undefined) {
      json.displayFields = displayFieldsToObjects(this.displayFields);
    }

    if (this.comps !== undefined) {
      json.comps = this.comps.map((comp) => ({
        ...comp,
        displayFields: displayFieldsToObjects(comp.displayFields),
      }));
    }

    return json;
  }

  /**
   * Sets the highlight state of the graphic to the given value.
   * @param {boolean} highlighted The new highlight state of the graphic.
   * @return {void}
   */
  setHighlight(highlighted) {
    console.warn('Graphic.setHighlight() is not implemented for', this);
  }

  /**
   * Sets the displayField state of the graphic to the given value.
   * @param {DisplayField[]} displayFields The new displayField state of the graphic.
   * @return {void}
   */
  setDisplayFields(displayFields) {
    this.displayFields.splice(0, this.displayFields.length, ...displayFields);
  }

  /**
   * Sets the comps of the graphic to the given value.
   * @param {Comp[]} comps The new comp state of the graphic.
   * @return {void}
   */
  setComps(comps) {
    this.comps = [...comps];
  }

  /**
   * Sets the editing state of the graphic. When `setEditing()` is called with `false` to turn off editing, the function
   * returns `true` if there are any pending unsaved changes. This allows the consumer to unsubscribe and stop
   * editing immediately while still being notified of any unsaved changes. The consumer can then call `toJSON()` to get
   * the final state of the graphic if desired.
   * @param {boolean} editing
   * @param {Partial<EditingContext>} [context]
   * @return {boolean|Promise<boolean>}
   */
  setEditing(editing, context) {
    console.warn('Graphic.setEditing() is not implemented for', this);
    return false;
  }

  /**
   * Sets the z-index of the DOM element responsible for the visual representation of this graphic. This is used to
   * ensure that graphics are drawn in the correct order and facilitate click event ordering.
   * This must be implemented by all types that are clickable.
   * @param {number} zIndex
   * @return {void}
   */
  setZIndex(zIndex) {
    console.warn('Graphic.setZIndex() is not implemented for', this);
  }

  /**
   * Displays the location of the graphic in some way. We want to provided visual feedback after
   * automatically panning and zooming to a feature on the map.
   * @return {void}
   */
  displayLocation() {}
  /**
   * Returns a Lat/Lng location for the graphic. This can be used when the location is needed but
   * a click event wasn't provided.
   * @return {{lat: number, lng: number}}
   */
  getLocation() {
    console.warn('Graphic.getLocation() is not implemented for', this.constructor.name);
    return {lat: 0, lng: 0};
  }

  /**
   * Checks if the token for the layer is expired.
   * @return {void}
   */
  tokenExpired() {
    return false;
  }
}

// renderQueue is used to batch attribute and style changes to the DOM. This is necessary because changing the DOM is
// expensive and can cause performance issues. This queue will be processed during the next animation frame.
// Because we cannot intercept the Google Maps call to `requestAnimationFrame` where it calls `draw()`, this will
// result in our render being one frame behind when used from within a Google Maps OverlayView `draw()` method.
const renderQueue = [];
let renderInProgress = false;

/**
 * Track items to be added to the DOM in the next animation frame. Be cautious with this when used with Google Maps. If
 * this function is called from an `OverlayView.draw()` method or otherwise triggered by map events, the render will be
 * one frame behind since we cannot hook directly into Google's `requestAnimationFrame` call before it happens.
 * @param {Function} item
 */
const addDomMutation = (item) => {
  renderQueue.push(item);
  if (!renderInProgress) {
    renderInProgress = true;
    render();
  }
};

/**
 * Render all items in the queue during the next animation frame
 */
const render = () => {
  requestAnimationFrame(() => {
    while (renderQueue.length) {
      renderQueue.shift()();
    }
    renderInProgress = false;
  });
};

export {
  Graphic,
  addDomMutation,
};
